import { FilterDropdown, FilterWrapper } from "@/components/styled";
import {
  useGetProjectRolesByIdsMutation,
  useSuggestProjectRolesMutation,
} from "@/services";
import { Button, Select, Space, Spin } from "antd";
import DOMPurify from "dompurify";
import { arrayOf, func, object, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const { Option } = Select;

const RoleSearchFilter = ({
  inputRef,
  onSearch,
  onReset,
  confirm,
  defaultValue,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState(defaultValue);

  const [
    suggestProjectRoles,
    {
      data: suggestedProjectRoles,
      isFetching: fetchingProjectRolesSuggestions,
    },
  ] = useSuggestProjectRolesMutation();
  const [
    getProjectRolesByIds,
    { data: projectRolesByIds, isLoading: loadingProjectRoles },
  ] = useGetProjectRolesByIdsMutation();

  useEffect(() => {
    if (!projectRolesByIds?.length) return;

    const suggests = projectRolesByIds.map(({ id, name }) => ({
      value: id,
      label: name,
    }));

    setValue(suggests);
  }, [projectRolesByIds]);

  useEffect(() => {
    if (!defaultValue?.length) return;

    getProjectRolesByIds(defaultValue);
  }, [defaultValue, getProjectRolesByIds]);

  const handleSearch = (selectedValue) => {
    setValue(selectedValue);
    onSearch(selectedValue);
  };

  const handleReset = () => {
    setValue();
    confirm();
    onReset();
  };

  const suggestRoles = (value) => {
    suggestProjectRoles({
      query: value,
      includeEmptyProjectType: true,
    });
  };

  return (
    <FilterWrapper>
      <FilterDropdown
        ref={inputRef}
        mode="multiple"
        showSearch
        defaultActiveFirstOption={false}
        optionLabelProp="label"
        filterOption={false}
        allowClear
        data-test-id="filter-dropdown"
        placeholder={intl.formatMessage({
          id: "tableFilters.dropDownPlaceholder",
        })}
        value={value}
        onSearch={suggestRoles}
        onChange={handleSearch}
        notFoundContent={
          fetchingProjectRolesSuggestions || loadingProjectRoles ? (
            <Spin size="small" />
          ) : null
        }
      >
        {suggestedProjectRoles?.map(({ document: { id, name } }) => (
          <Option key={id}>
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(name),
              }}
            />
          </Option>
        ))}
      </FilterDropdown>
      <Space>
        <Button data-test-id="reset-button" onClick={handleReset} size="small">
          {intl.formatMessage({ id: "general.reset" })}
        </Button>
      </Space>
    </FilterWrapper>
  );
};

RoleSearchFilter.propTypes = {
  inputRef: object,
  onSearch: func,
  onReset: func,
  confirm: func,
  defaultValue: arrayOf(string),
};

export default RoleSearchFilter;
